import { DataUrlToBlob } from "../../cad/utils/blob"
import { cropToContent } from "../../cad/utils/image"

const sleep = (ms) => new Promise(r => setTimeout(r, ms))

/**
 * Captura el viewer una vez por cada vista del array `views`.
 * @param {Object}   opts
 * @param {Object}   opts.viewer       — ref al componente Viewer con FitToScreen/Save
 * @param {Array}    opts.views        — [{code, name, ...}]
 * @param {Function} opts.applyView    — (code) => void  cambia la vista activa
 * @param {Function} opts.onPreview    — (dataUrl) => void  notifica preview en vivo
 * @param {Number}   opts.renderDelay  — ms espera tras aplicar vista (default 400)
 * @param {Number}   opts.captureDelay — ms espera entre capturas (default 200)
 * @returns {Promise<Array<{name, src: Blob}>>}
 */
/**
 * Captura una sola toma del viewer, sin aplicar vistas. Pensado para
 * electrical/trenches donde no hay flow de "vistas" como en mecánico — la
 * imagen sirve solo como preview del pictograma en listados.
 *
 * @param {Object}   opts
 * @param {Object}   opts.viewer       — ref al componente Viewer con FitToScreen/Save
 * @param {Function} opts.onPreview    — (dataUrl) => void  notifica preview en vivo
 * @param {Number}   opts.renderDelay  — ms espera tras FitToScreen (default 400)
 * @param {String}   opts.name         — nombre del archivo resultante (default 'preview.png')
 * @returns {Promise<Array<{name, src: Blob}>>}  array de 0 o 1 elemento
 */
export async function captureViewerSnapshot(opts) {
  const renderDelay = opts.renderDelay ?? 400
  const name        = opts.name        ?? 'preview.png'

  opts.viewer.FitToScreen()
  await sleep(renderDelay)

  const rawDataUrl = opts.viewer.Save({ scale: 2, transparent: true })
  if (!rawDataUrl) return []

  const dataUrl = await cropToContent(rawDataUrl, { padding: 15 })
  if (!dataUrl) return []

  opts.onPreview?.(dataUrl)
  return [{ name, src: DataUrlToBlob(dataUrl) }]
}

export async function captureViewerByViews(opts) {
  const renderDelay  = opts.renderDelay  ?? 400
  const captureDelay = opts.captureDelay ?? 200
  const result = []

  for (const view of opts.views) {
    opts.applyView(view.code)
    opts.viewer.FitToScreen()
    await sleep(renderDelay)

    // scale: 2 → captura al doble de resolución para que zoom se vea nítido.
    // transparent: true → fondo PNG transparente en lugar de blanco opaco.
    const rawDataUrl = opts.viewer.Save({ scale: 2, transparent: true })
    if (!rawDataUrl) continue

    // Recorta el canvas al contenido visible + padding 15px en cada lado.
    // Si la vista no tiene contenido (toda en fondo blanco), cropToContent devuelve
    // null y omitimos esa captura — no se sube como imagen vacía.
    const dataUrl = await cropToContent(rawDataUrl, { padding: 15 })
    if (!dataUrl) continue

    opts.onPreview?.(dataUrl)
    result.push({ name: view.code + '.png', src: DataUrlToBlob(dataUrl) })
    await sleep(captureDelay)
  }
  return result
}
